/**
 * @fileoverview Server-side DataTables renderer
 *
 * Renders tables using DataTables with server-side processing.
 * Best for very large datasets (>10,000 rows) where client-side
 * processing would be too slow.
 *
 * @module renderers/ServerSideRenderer
 */

import { BaseRenderer } from './BaseRenderer.js';
import { DEFAULTS } from '../constants.js';

/**
 * ServerSideRenderer - Server-side DataTables processing
 * @class
 * @extends BaseRenderer
 */
export class ServerSideRenderer extends BaseRenderer {
  /**
   * Create a ServerSideRenderer instance
   * @param {import('../types.js').RenderContext} context - Render context
   */
  constructor(context) {
    super(context);
    this.dataTable = null;

    if (!this.options.ajax) {
      throw new Error('ServerSideRenderer: ajax option is required for server-side processing');
    }
  }

  /**
   * Render table with server-side DataTables
   * @override
   * @returns {Promise<void>}
   */
  async render() {
    if (!this.isDataTablesAvailable()) {
      throw new Error('ServerSideRenderer: DataTables library not available');
    }

    this.startRenderTiming();

    // Ensure table element exists
    this.ensureTableElement();

    // Apply modern styling
    this.applyModernStyling();

    // Build DataTables configuration
    const dtConfig = this._buildDataTablesConfig();

    try {
      console.log('ServerSideRenderer: Initializing server-side table');
      this.dataTable = jQuery(this.tableElement).DataTable(dtConfig);

      this.wrapTableForScroll();

      this.endRenderTiming();
      this.isRendered = true;

      // Store refresh method for server-side tables
      this.refresh = () => {
        if (this.dataTable && this.dataTable.ajax) {
          this.dataTable.ajax.reload();
        }
      };

      // Add processing class toggle for backdrop effect
      jQuery(this.tableElement).on('processing.dt', (e, settings, processing) => {
        const wrapper = jQuery(e.target).closest('.dataTables_wrapper');
        if (processing) {
          wrapper.addClass('processing');
        } else {
          wrapper.removeClass('processing');
        }
      });

      // Fix column alignment after each draw
      jQuery(this.tableElement).on('draw.dt', () => {
        if (this.dataTable && this.dataTable.columns) {
          // Delay adjustment to ensure container is visible
          setTimeout(() => {
            this.dataTable.columns.adjust();
          }, 50);
        }
      });

      // Fix alignment when container becomes visible
      this._setupVisibilityHandler();

      // Call init callback
      if (this.options.onInit) {
        this.options.onInit(this);
      }

    } catch (error) {
      console.error('ServerSideRenderer: Initialization failed:', error);
      this.showError('Failed to initialize table: ' + error.message);
      throw error;
    }
  }

  /**
   * Build DataTables configuration for server-side processing
   * @private
   * @returns {Object} DataTables config
   */
  _buildDataTablesConfig() {
    // Determine sort order - validate against columns if provided
    let order = this.options.order || DEFAULTS.DEFAULT_ORDER;
    const columns = this.options.columns;
    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      order = [];
    } else if (Array.isArray(order) && order.length > 0) {
      // Validate that order references existing columns
      order = order.filter(o => Array.isArray(o) && o[0] < columns.length);
    }

    const config = {
      serverSide: true,
      processing: true,
      ajax: this.options.ajax,
      pageLength: this.options.pageLength || DEFAULTS.PAGE_LENGTH,
      lengthMenu: this.options.lengthMenu || DEFAULTS.LENGTH_MENU,
      searching: this.options.searching !== false,
      ordering: this.options.ordering !== false,
      paging: this.options.paging !== false,
      info: this.options.info !== false,
      dom: this.options.dom || DEFAULTS.DOM_LAYOUT,
      order: order,
      autoWidth: false, // Always false for server-side to prevent misalignment
      destroy: true,
      language: {
        processing: "Loading data...",
        ...(this.options.language || {})
      }
    };

    // Add columns if provided
    if (columns && Array.isArray(columns) && columns.length > 0) {
      config.columns = columns;
    }

    // Add column definitions if provided
    if (this.options.columnDefs) {
      config.columnDefs = this.options.columnDefs;
    }

    // Don't use scrollY for server-side tables - pagination handles height
    if (this.options.scrollY !== false) {
      // Only add if explicitly set
      if (this.options.scrollY) {
        config.scrollY = this.options.scrollY;
        config.scrollCollapse = false;
      }
    }

    if (this.options.select) {
      config.select = this.options.select;
    }

    if (this.options.buttons) {
      config.buttons = this.options.buttons;
    }

    // Callbacks
    config.drawCallback = this.options.drawCallback;

    // Wrap rowCallback for row click handler
    if (this.options.onRowClick) {
      const originalRowCallback = this.options.rowCallback;
      config.rowCallback = (row, data, index) => {
        if (originalRowCallback) {
          originalRowCallback(row, data, index);
        }
        jQuery(row).on('click', () => {
          this.options.onRowClick(data, row, index);
        });
      };
    } else {
      config.rowCallback = this.options.rowCallback;
    }

    // Wrap initComplete for export buttons
    const userInitComplete = this.options.initComplete || this.options.fnInitComplete;
    config.initComplete = function(settings, json) {
      if (userInitComplete) {
        // Create a context object with api() method since we're overriding DataTables' native this
        var api = new jQuery.fn.dataTable.Api(settings);
        var context = {
          api: function() { return api; }
        };
        userInitComplete.call(context, settings, json);
      }
      // Export buttons would be added here by ExportManager
      if (this.options && this.options.onInitComplete) {
        this.options.onInitComplete(settings, json);
      }
    }.bind({ options: this.options });

    // Legacy callers configure the underlying DataTable with its native,
    // nested `dataTable: {...}` option shape (e.g. rowId, columns,
    // oLanguage) rather than this renderer's flat per-option keys above.
    // Nothing above reads that nested object, so those settings were
    // silently dropped. Merge it in last so it can override the flat
    // defaults, matching what the caller explicitly asked for. See the
    // matching fix in DataTablesRenderer.js/DomTableRenderer.js.
    if (this.options.dataTable) {
      Object.assign(config, this.options.dataTable);
    }

    return config;
  }

  /**
   * Get DataTable instance
   * @returns {Object|null} DataTable instance
   */
  getDataTable() {
    return this.dataTable;
  }

  /**
   * Setup handlers to fix column alignment when table becomes visible
   * @private
   */
  _setupVisibilityHandler() {
    if (!this.dataTable) return;

    const adjustColumns = () => {
      if (this.dataTable && this.dataTable.columns) {
        this.dataTable.columns.adjust().draw(false);
      }
    };

    // Bootstrap tab shown event
    const tabSelector = `a[href="#${this.container.id}"], a[href="#${this.container.parentElement?.id}"]`;
    jQuery(document).on('shown.bs.tab', tabSelector, () => {
      setTimeout(adjustColumns, 100);
    });

    // Bootstrap collapse shown event
    jQuery(this.container).on('shown.bs.collapse', () => {
      setTimeout(adjustColumns, 100);
    });

    // Generic visibility change (for other UI frameworks)
    if (this.container) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const isVisible = this.container.offsetParent !== null;
            if (isVisible) {
              setTimeout(adjustColumns, 100);
            }
          }
        });
      });

      observer.observe(this.container, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      // Store observer for cleanup
      this._visibilityObserver = observer;
    }

    // Also adjust on window resize
    jQuery(window).on('resize.datatable', () => {
      adjustColumns();
    });
  }

  /**
   * Refresh table (reload from server)
   * Note: This is overridden in render() method
   */
  refresh() {
    if (this.dataTable && this.dataTable.ajax) {
      this.dataTable.ajax.reload();
    }
  }

  /**
   * Clear table data
   */
  clear() {
    if (this.dataTable) {
      this.dataTable.clear().draw();
    }
  }

  /**
   * Search table
   * @param {string} term - Search term
   */
  search(term) {
    if (this.dataTable) {
      this.dataTable.search(term).draw();
    }
  }

  /**
   * Sort table
   * @param {number} columnIndex - Column index
   * @param {string} direction - Sort direction ('asc' or 'desc')
   */
  sort(columnIndex, direction) {
    if (this.dataTable) {
      this.dataTable.order([columnIndex, direction]).draw();
    }
  }

  /**
   * Get current page data (server-side doesn't have all data client-side)
   * @returns {Array}
   */
  getData() {
    if (this.dataTable) {
      return this.dataTable.rows({ page: 'current' }).data().toArray();
    }
    return [];
  }

  /**
   * Get all data (requires server endpoint)
   * For full data export, use exportEndpoint option
   * @returns {Array} Current page data only
   */
  getAllData() {
    console.warn('ServerSideRenderer: getAllData() returns current page only. Use exportEndpoint for full data.');
    return this.getData();
  }

  /**
   * Get selected rows
   * @returns {Array}
   */
  getSelectedRows() {
    if (this.dataTable) {
      return this.dataTable.rows('.selected').data().toArray();
    }
    return [];
  }

  /**
   * Destroy renderer
   * @override
   */
  destroy() {
    // Remove DataTables event listeners
    if (this.tableElement) {
      jQuery(this.tableElement).off('processing.dt');
      jQuery(this.tableElement).off('draw.dt');
    }

    // Remove window resize handler
    jQuery(window).off('resize.datatable');

    // Disconnect visibility observer
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }

    // Remove tab shown listeners
    jQuery(document).off('shown.bs.tab');

    if (this.dataTable) {
      try {
        this.dataTable.destroy();
      } catch (error) {
        console.warn('ServerSideRenderer: Error destroying DataTable:', error);
      }
      this.dataTable = null;
    }

    super.destroy();
  }

  /**
   * Proxy methods to DataTable API
   */

  column(...args) {
    return this.dataTable ? this.dataTable.column(...args) : null;
  }

  columns(...args) {
    return this.dataTable ? this.dataTable.columns(...args) : null;
  }

  row(...args) {
    return this.dataTable ? this.dataTable.row(...args) : null;
  }

  rows(...args) {
    return this.dataTable ? this.dataTable.rows(...args) : null;
  }

  cell(...args) {
    return this.dataTable ? this.dataTable.cell(...args) : null;
  }

  cells(...args) {
    return this.dataTable ? this.dataTable.cells(...args) : null;
  }

  draw(...args) {
    return this.dataTable ? this.dataTable.draw(...args) : null;
  }

  page(...args) {
    return this.dataTable ? this.dataTable.page(...args) : null;
  }

  order(...args) {
    return this.dataTable ? this.dataTable.order(...args) : null;
  }
}

export default ServerSideRenderer;
